import React, { useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { billsApi, BillAddressBackfillResult } from '../../api/bills';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../hooks/useToast';

/**
 * Fills the service address on bill payments made before it was captured.
 *
 * The bill receipt doubles as a utility bill for address verification, so an
 * older receipt with a blank address is not much use as evidence. This looks
 * the address up from the bills provider, one call per distinct meter.
 *
 * Two deliberate frictions, both because this spends provider calls:
 *  • Check first is the primary action; the real run is secondary.
 *  • A run is capped, so it is repeatable rather than one long job.
 */

const LIMIT_OPTIONS = [25, 50, 100, 250, 500];

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div style={{ flex: '1 1 150px', minWidth: 150 }}>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--neutral-900)' }}>
      {value}
    </div>
    {hint ? (
      <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>
        {hint}
      </div>
    ) : null}
  </div>
);

export const BillAddressBackfill: React.FC = () => {
  usePage('Bill Addresses');
  const toast = useToast();

  const [limit, setLimit] = useState(100);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BillAddressBackfillResult | null>(null);

  async function run(dryRun: boolean) {
    const setBusy = dryRun ? setChecking : setRunning;
    try {
      setBusy(true);
      const res = await billsApi.backfillAddresses({
        dryRun,
        limit: dryRun ? undefined : limit,
      });
      setResult(res);
      if (dryRun) {
        toast.info(
          res.metersPending === 0
            ? 'Nothing outstanding — every bill that can have an address has one.'
            : `${res.metersPending} meter(s) outstanding, covering ${res.rowsPending} bill(s).`,
        );
      } else if (res.metersFilled === 0 && res.metersAttempted === 0) {
        toast.info('Nothing left to backfill.');
      } else {
        toast.success(
          `Filled ${res.rowsUpdated} bill(s) across ${res.metersFilled} meter(s).`,
        );
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Backfill failed';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  const pending = result?.metersPending ?? null;
  const busy = checking || running;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--neutral-900)',
          }}
        >
          Bill service addresses
        </h3>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--neutral-600)',
          }}
        >
          Bill receipts are used as utility bills for address verification.
          Payments made before the address was captured show no address — this
          looks it up from the provider, <strong>one call per meter</strong>, so a
          meter paid monthly for years costs a single lookup.
        </p>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--neutral-600)',
          }}
        >
          Anything filled here is recorded as <strong>backfilled</strong> rather
          than verified at payment: a lookup returns the address registered to
          that meter <em>today</em>, which is a fair reconstruction but not a
          fact confirmed at the time of payment. Addresses already captured at
          payment are never overwritten. Data top-ups are skipped — they resolve
          no customer, so there is no address to find.
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 20,
            flexWrap: 'wrap',
          }}
        >
          <Button onClick={() => void run(true)} loading={checking} disabled={busy}>
            Check what's outstanding
          </Button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
              Meters per run
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              disabled={busy}
              style={{
                height: 38,
                padding: '0 12px',
                fontSize: 13,
                fontFamily: 'var(--font-family)',
                color: 'var(--neutral-700)',
                background: 'var(--neutral-100)',
                border: '1px solid var(--neutral-300)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="secondary"
            onClick={() => void run(false)}
            loading={running}
            // Only offered once a check has shown there is work to do — it
            // spends provider calls, so it should not be the reflex click.
            disabled={busy || pending === null || pending === 0}
          >
            Run backfill
          </Button>

          {pending === null ? (
            <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
              Check first to see the scale.
            </span>
          ) : null}
        </div>
      </Card>

      {result ? (
        <Card>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--neutral-900)',
              }}
            >
              {result.dryRun ? 'Outstanding' : 'Last run'}
            </h3>
            <Badge variant={result.dryRun ? 'info' : 'success'}>
              {result.dryRun ? 'Dry run — nothing changed' : 'Applied'}
            </Badge>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Stat
              label="Meters outstanding"
              value={result.metersPending}
              hint={`${result.rowsPending} bill(s)`}
            />
            {!result.dryRun ? (
              <>
                <Stat label="Meters looked up" value={result.metersAttempted} />
                <Stat
                  label="Meters filled"
                  value={result.metersFilled}
                  hint={`${result.rowsUpdated} bill(s) updated`}
                />
                <Stat
                  label="Failed"
                  value={result.metersFailed}
                  hint={result.metersFailed ? 'skipped, not fatal' : undefined}
                />
              </>
            ) : null}
          </div>

          {!result.dryRun && result.metersPending > result.metersAttempted ? (
            <p
              style={{
                margin: '16px 0 0',
                fontSize: 13,
                color: 'var(--neutral-600)',
              }}
            >
              More meters remain than this run covered. Check again and run
              until nothing is outstanding — it is safe to repeat.
            </p>
          ) : null}

          {result.failures.length ? (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--neutral-800)',
                  marginBottom: 8,
                }}
              >
                Meters that could not be looked up
              </div>
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 12,
                  color: 'var(--neutral-500)',
                }}
              >
                Usually a biller no longer in the live catalog, or a meter the
                provider no longer recognises. These are skipped, not retried.
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 12,
                  color: 'var(--neutral-600)',
                }}
              >
                {result.failures.slice(0, 20).map((f, i) => (
                  <li key={`${f.billerId}-${i}`} style={{ marginBottom: 4 }}>
                    <code>{f.billerId}</code> — {f.reason}
                  </li>
                ))}
              </ul>
              {result.failures.length > 20 ? (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--neutral-500)',
                    marginTop: 6,
                  }}
                >
                  …and {result.failures.length - 20} more.
                </div>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
};
